import * as T from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

import { toonMaterial } from './art-direction.js';
const GEO=new Map();
const UP=new T.Vector3(0,1,0);
export const C={white:'#fff6e6',snow:'#e7f8ff',skin:'#ffc69c',dark:'#17263b',gold:'#ffd271',red:'#e74957',green:'#27875d',navy:'#233654',ice:'#86dbef'};
export const material = toonMaterial;

function geometry(key,make){if(!GEO.has(key))GEO.set(key,make());return GEO.get(key);}
export function mesh(parent,g,color,x=0,y=0,z=0,sx=1,sy=sx,sz=sx,metal=0,glow=0){
  const m=new T.Mesh(g,material(color,metal,glow));m.position.set(x,y,z);m.scale.set(sx,sy,sz);m.castShadow=true;m.receiveShadow=true;parent.add(m);return m;
}
export const ball=(p,c,x,y,z,sx=.5,sy=sx,sz=sx,metal=0,glow=0)=>mesh(p,geometry('sphere',()=>new T.SphereGeometry(1,16,12)),c,x,y,z,sx,sy,sz,metal,glow);
export const box=(p,c,x,y,z,sx,sy,sz,metal=0)=>mesh(p,geometry('box',()=>new RoundedBoxGeometry(1,1,1,2,.13)),c,x,y,z,sx,sy,sz,metal);
export const cyl=(p,c,x,y,z,rt,rb,h,n=12,metal=0,glow=0)=>mesh(p,geometry(`c:${rt}:${rb}:${h}:${n}`,()=>new T.CylinderGeometry(rt,rb,h,n)),c,x,y,z,1,1,1,metal,glow);
export const ring=(p,c,x,y,z,r,t=.06,metal=0)=>mesh(p,geometry(`t:${r}:${t}`,()=>new T.TorusGeometry(r,t,6,24)),c,x,y,z,1,1,1,metal);
export function link(p,c,a,b,r=.08){const va=new T.Vector3(...a),vb=new T.Vector3(...b),mid=va.clone().add(vb).multiplyScalar(.5);const m=cyl(p,c,mid.x,mid.y,mid.z,r,r,va.distanceTo(vb));m.quaternion.setFromUnitVectors(UP,vb.sub(va).normalize());return m;}
export function tube(p,c,points,r=.07){const curve=new T.CatmullRomCurve3(points.map(a=>new T.Vector3(...a)));return mesh(p,new T.TubeGeometry(curve,24,r,7,false),c);}
export function star(p,c,x,y,z,r=.25,depth=.09){
  const s=new T.Shape();for(let i=0;i<10;i++){const a=i*Math.PI/5+Math.PI/2,rr=i%2?r*.45:r;const xx=Math.cos(a)*rr,yy=Math.sin(a)*rr;i?s.lineTo(xx,yy):s.moveTo(xx,yy);}s.closePath();
  const g=geometry(`star:${r}:${depth}`,()=>new T.ExtrudeGeometry(s,{depth,bevelEnabled:true,bevelSegments:1,steps:1,bevelSize:.025,bevelThickness:.02}));
  return mesh(p,g,c,x,y,z,1,1,1,.45,.06);
}
export function eyes(p,y=.08,z=.44,separation=.21,color=C.dark,angry=false){
  for(const s of [-1,1]){
    ball(p,C.white,s*separation,y,z,.15,.15,.07);
    ball(p,color,s*separation,y-.005,z+.06,.067,.083,.035,0,.06);
    ball(p,C.white,s*separation-.023,y+.035,z+.085,.02);
    if(angry){const b=box(p,C.dark,s*separation,y+.14,z+.025,.26,.065,.065);b.rotation.z=s*.21;}
  }
}
export function fur(p,x,y,z,r,count=10){for(let i=0;i<count;i++){const a=i/count*Math.PI*2;ball(p,C.white,x+Math.cos(a)*r,y,z+Math.sin(a)*r,.12,.10,.10);}}
export function scarf(body,color,y=2){cyl(body,color,0,y,0,.47,.44,.19);const tail=box(body,color,.3,y-.35,.39,.22,.6,.11);tail.rotation.z=-.16;box(body,C.white,.3,y-.57,.41,.24,.055,.13);}
export function boots(r,color=C.dark){for(const s of [-1,1]){const l=s<0?r.ll:r.rl;box(l,color,0,-.46,.12,.42,.32,.67);cyl(l,C.gold,0,-.30,0,.19,.19,.065,12,.4);}}
export function limbs(r,sleeve,legs=sleeve,glove=C.white){
  for(const a of [r.la,r.ra]){cyl(a,sleeve,0,-.26,0,.18,.23,.54);ball(a,glove,0,-.66,.055,.235,.25,.23);cyl(a,C.white,0,-.51,0,.20,.20,.12);}
  for(const l of [r.ll,r.rl])cyl(l,legs,0,-.2,0,.17,.19,.45);
  boots(r);
}
export function coat(r,color){ball(r.body,color,0,1.47,0,.70,.83,.47);cyl(r.body,color,0,1.06,0,.62,.67,.5);}
export function belt(body,y=1.25){box(body,C.dark,0,y,.055,1.31,.20,.88);box(body,C.gold,0,y,.52,.31,.25,.09,.65);box(body,C.dark,0,y,.58,.15,.12,.035);}
export function humanHead(r,{beard=false,old=false,angry=false}={}){
  ball(r.head,C.skin,0,.04,0,.47,.52,.41);ball(r.head,C.skin,0,-.04,.42,.11,.12,.12);eyes(r.head,.10,.37,.19,C.dark,angry);
  for(const s of [-1,1])ball(r.head,C.skin,s*.47,.02,0,.10,.17,.12);
  if(beard){ball(r.head,C.white,0,-.3,.28,.43,.32,.30);for(const s of [-1,1]){const m=ball(r.head,C.white,s*.16,-.12,.45,.22,.10,.09);m.rotation.z=s*.16;}}
  else {const mouth=box(r.head,'#9a4f43',0,-.22,.38,.15,.035,.025);mouth.rotation.z=angry?.1:0;}
  if(old)for(const s of [-1,1]){ring(r.head,C.gold,s*.19,.10,.47,.16,.022,.8);}
}
export function gun(arm,color,length=.85){
  box(arm,color,0,-.52,.28,.38,.31,.62,.2);
  const barrel=cyl(arm,C.dark,0,-.49,.63,.16,.16,length,12,.45);barrel.rotation.x=Math.PI/2;
  const muzzle=ring(arm,C.gold,0,-.49,.63+length/2,.18,.04,.8);
  ball(arm,'#92f6ff',0,-.49,muzzle.position.z-.01,.12,.12,.03,.1,.4);
}
export function antler(p,x,y,z,s){
  const points=[[x,y,z],[x+s*.18,y+.28,z-.04],[x+s*.35,y+.60,z-.06],[x+s*.42,y+.85,z-.12]];
  tube(p,'#e8cc94',points,.065);
  link(p,'#e8cc94',points[1],[x+s*.48,y+.35,z+.04],.047);
  link(p,'#e8cc94',points[2],[x+s*.15,y+.77,z-.1],.048);
  link(p,'#e8cc94',points[2],[x+s*.65,y+.76,z-.11],.04);
}
export function candy(p,points,r=.11){
  const curve=new T.CatmullRomCurve3(points.map(v=>new T.Vector3(...v)));
  const g=new T.TubeGeometry(curve,60,r,10,false),colors=[];
  const red=new T.Color('#ea4655'),white=new T.Color('#fff9ec');
  for(let i=0;i<g.attributes.position.count;i++){const u=g.attributes.uv.getX(i),v=g.attributes.uv.getY(i),c=(Math.floor(u*15+v*.8)%2===0)?red:white;colors.push(c.r,c.g,c.b);}
  g.setAttribute('color',new T.Float32BufferAttribute(colors,3));
  const m=new T.Mesh(g,toonMaterial('#ffffff', .08, 0, true));m.castShadow=true;p.add(m);return m;
}
export function makeRig(){
  const root=new T.Group(),body=new T.Group(),head=new T.Group(),la=new T.Group(),ra=new T.Group(),ll=new T.Group(),rl=new T.Group();
  root.add(body,head,la,ra,ll,rl);head.position.y=2.25;la.position.set(-.69,1.98,0);ra.position.set(.69,1.98,0);ll.position.set(-.29,.79,0);rl.position.set(.29,.79,0);
  return {root,body,head,la,ra,ll,rl};
}
/** Merge static details by material, retaining six independently animated body groups. */
export function compact(group){
  group.updateMatrixWorld(true);const inv=group.matrixWorld.clone().invert(),batches=new Map();
  group.traverse(o=>{if(o.isMesh){const key=o.material.uuid;if(!batches.has(key))batches.set(key,{material:o.material,geometries:[]});batches.get(key).geometries.push((o.geometry.index?o.geometry.toNonIndexed():o.geometry.clone()).applyMatrix4(inv.clone().multiply(o.matrixWorld)));}});
  group.clear();
  for(const {material:mat,geometries} of batches.values()){
    const g=mergeGeometries(geometries,false);geometries.forEach(a=>a.dispose());if(!g)continue;
    const m=new T.Mesh(g,mat);m.castShadow=true;m.receiveShadow=true;group.add(m);
  }
}
